import { useTranslator } from '@translator-app/react';
import { useUserContext } from '~/contexts/user-context';
import { useScreenSize } from '~/hooks/use-screen-size';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron-right.svg';
import classNames from 'classnames';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

export interface IRoute {
  translationKey: string;
  href: string;
  as?: string;
  role: number;
  items?: Array<IRoute>;
}

export type Props = {
  routes: Array<IRoute>;
};

export const SideBar: React.FC<Props> = (props) => {
  let { routes } = props;
  let router = useRouter();
  let { highestRole } = useUserContext();
  let { translate } = useTranslator();
  let screenSize = useScreenSize();
  let [showSidebar, setShowSidebar] = React.useState(() => screenSize.width > 1000);

  let filteredRoutes: Array<IRoute> = React.useMemo(() => {
    return routes
      .filter((r) => r.role <= highestRole)
      .map((route) => {
        let items: Array<IRoute> = route.items || [];
        return {
          ...route,
          items: items.filter((r) => r.role <= highestRole),
        };
      });
  }, [highestRole, routes]);

  let activeRoute = React.useMemo(() => {
    let sortedRoutes = routes.sort((a, b) => {
      return b.href.length - a.href.length;
    });

    for (let route of sortedRoutes) {
      if (router.pathname.startsWith(route.href)) {
        return route.href;
      }
    }

    return null;
  }, [router.pathname]);

  return (
    <div className="flex flex-col bg-gray-100 border-r h-full whitespace-nowrap text-gray-800">
      <div className="h-full overflow-y-auto">
        {showSidebar &&
          filteredRoutes.map((route, i) => {
            let rootPathIsActive = route.href === activeRoute;

            return (
              <div key={route.href}>
                <NextLink href={route.href} as={route.as} key={route.href} passHref>
                  <a
                    href="#"
                    className={classNames('block px-4 py-2 font-medium cursor-pointer hover:text-blue-500', {
                      'bg-gray-300': rootPathIsActive,
                    })}
                  >
                    {translate(route.translationKey)}
                  </a>
                </NextLink>
                {route.items.length > 0 && rootPathIsActive && (
                  <div className="mt-1">
                    {route.items.map((item) => {
                      let subPathIsActive = router.pathname.startsWith(item.href);

                      return (
                        <NextLink href={item.href} as={item.as} key={item.href} passHref>
                          <a
                            href="#"
                            className={classNames('block px-8 py-1 cursor-pointer hover:text-blue-500', {
                              'font-medium': subPathIsActive,
                            })}
                          >
                            {translate(item.translationKey)}
                          </a>
                        </NextLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

        <div className="h-6"></div>

        {showSidebar && (
          <div
            className="flex items-center pl-4 py-4 pr-12 uppercase font-medium text-sm text-gray-400 hover:text-blue-400 cursor-pointer select-none"
            onClick={() => {
              setShowSidebar(false);
            }}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            <span>{translate('sidebar.hide')}</span>
          </div>
        )}

        {!showSidebar && (
          <div
            className="flex items-center h-full px-4 text-gray-400 hover:text-blue-400 cursor-pointer select-none"
            onClick={() => {
              setShowSidebar(true);
            }}
          >
            <ChevronRight className="w-4 h-4 mr-1" />
          </div>
        )}
      </div>
    </div>
  );
};
